import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Upload,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Bell,
  Loader2,
  Trash2,
  Video,
  Sparkles,
  Download,
  ListVideo,
  ClipboardPen,
} from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import { useAppDispatch } from '@/hooks/useRedux'
import { uploadVideoForMilestone } from '@/redux/campaignThunks'
import {
  useSubmitMilestoneMutation,
  useDeleteMilestoneSubmissionMutation,
  useGetVideoSuggestionsQuery,
  useUpdateDeliverablesMutation,
} from '@/redux/collaborationsApi'
import { SubmissionTimeline } from '@/components/SubmissionTimeline'
import { getMilestoneByName } from '@adthlete/common/utils/milestoneConfig'
import { useCampaignDetail } from '@/hooks/useCampaignDetail'
import { downloadRemoteFile, inferFileNameFromUrl } from '@/lib/download'
import VideoSuggestionSheet, {
  VideoSuggestion,
} from '@/components/collaboration-detail/VideoSuggestionSheet'
import AiCaptionAssistantChat from '@/components/collaboration-detail/AiCaptionAssistantChat'
import {
  DeliverableBuilder,
  createDefaultDeliverable,
  parseContentUrls,
  parseReviewNotes,
  getDeliverableVideoUrl,
  getDeliverableReviewNote,
} from '@adthlete/shared-frontend'
import type { DeliverableItem } from '@adthlete/shared-frontend'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ── Per-deliverable upload state ──────────────────────────────────────────────
interface DeliverableUploadState {
  selectedFile: File | null
  previewUrl: string | null
  caption: string
  acceptedSuggestion: VideoSuggestion | null
  uploading: boolean
  uploadProgress: number
  /** Local override for sameAsDeliverableId — null means unique content */
  sameAsDeliverableId: string | null
}

const emptyDeliverableState = (): DeliverableUploadState => ({
  selectedFile: null,
  previewUrl: null,
  caption: '',
  acceptedSuggestion: null,
  uploading: false,
  uploadProgress: 0,
  sameAsDeliverableId: null,
})

const UploadVideoReview = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [submitMilestone, { isLoading: isSubmitting }] = useSubmitMilestoneMutation()
  const [deleteMilestoneSubmission] = useDeleteMilestoneSubmissionMutation()
  const [updateDeliverables, { isLoading: isSavingDeliverables }] = useUpdateDeliverablesMutation()

  const { campaign, isLoading: detailLoading, refetch } = useCampaignDetail(id)

  const { data: videoSuggestionsData } = useGetVideoSuggestionsQuery(id!, { skip: !id })

  // Deliverable setup state (for when structured_deliverables is null/empty)
  const [draftDeliverables, setDraftDeliverables] = useState<DeliverableItem[]>([
    createDefaultDeliverable(),
  ])

  // Per-deliverable upload state
  const [deliverableStates, setDeliverableStates] = useState<
    Record<string, DeliverableUploadState>
  >({})
  const previewUrlsRef = useRef(new Set<string>())

  const [deleting, setDeleting] = useState(false)
  const [downloadingVideo, setDownloadingVideo] = useState(false)
  const [showSuggestionSheet, setShowSuggestionSheet] = useState<string | null>(null) // deliverable ID
  const [showCaptionChat, setShowCaptionChat] = useState<string | null>(null) // deliverable ID

  // Find the milestones
  const creativeMilestone = useMemo(() => {
    if (!campaign?.milestones) return null
    return campaign.milestones.find((m) => m.name.toLowerCase().includes('creative concept'))
  }, [campaign?.milestones])

  const videoReviewMilestone = useMemo(() => {
    if (!campaign?.milestones) return null
    return campaign.milestones.find((m) => {
      const definition = getMilestoneByName(m.name)
      return definition?.sequenceOrder === 2
    })
  }, [campaign?.milestones])

  const conceptSubmission = useMemo(() => {
    if (!creativeMilestone?.submissions) return null
    return [...creativeMilestone.submissions].sort((a: any, b: any) => b.version - a.version)[0]
  }, [creativeMilestone?.submissions])

  const isConceptApproved = conceptSubmission?.status === 'approved'
  const isConceptPendingReview = conceptSubmission?.status === 'pending_review'

  const activeVideoSubmission = useMemo(() => {
    if (!videoReviewMilestone?.submissions) return null
    return videoReviewMilestone.submissions.find(
      (sub: any) => sub.status === 'pending_review' || sub.status === 'approved',
    )
  }, [videoReviewMilestone?.submissions])

  const isVideoApproved = activeVideoSubmission?.status === 'approved'

  const revisionRequested = useMemo(() => {
    if (!videoReviewMilestone?.submissions) return null
    return videoReviewMilestone.submissions.find((sub: any) => sub.status === 'revision_requested')
  }, [videoReviewMilestone?.submissions])

  const isRevisionRequested = videoReviewMilestone?.status === 'revision_requested'

  // Structured deliverables from collaboration
  const structuredDeliverables: DeliverableItem[] | null = campaign?.structuredDeliverables ?? null
  const hasDeliverables =
    Array.isArray(structuredDeliverables) && structuredDeliverables.length > 0

  // Per-deliverable revision notes: reviewNotes.deliverables[id]
  const deliverableRevisionNotes = useMemo(() => {
    if (!isRevisionRequested || !revisionRequested?.reviewNotes) return null
    return parseReviewNotes(revisionRequested.reviewNotes)
  }, [isRevisionRequested, revisionRequested])

  const allVideoSuggestions = useMemo(
    () => videoSuggestionsData?.suggestions ?? [],
    [videoSuggestionsData],
  )

  // Returns the latest suggestion for a specific deliverable (or the first unassigned one if no deliverableId match)
  const getSuggestionForDeliverable = (deliverableId: string) => {
    const targeted = allVideoSuggestions.filter((s) => s.deliverableId === deliverableId)
    if (targeted.length > 0) return targeted[0]
    // Also surface suggestions with no deliverableId assigned (legacy / pre-multi-deliverable)
    const unassigned = allVideoSuggestions.filter((s) => !s.deliverableId)
    return unassigned.length > 0 ? unassigned[0] : null
  }

  // Initialise per-deliverable state and pre-fill captions from revision when applicable
  useEffect(() => {
    if (!hasDeliverables || !structuredDeliverables) return
    setDeliverableStates((prev) => {
      const next: Record<string, DeliverableUploadState> = {}
      for (const d of structuredDeliverables) {
        const existing = prev[d.id] ?? emptyDeliverableState()
        // Pre-fill caption when caption was approved in last revision
        if (deliverableRevisionNotes) {
          const dnotes = deliverableRevisionNotes[d.id]
          if (dnotes?.caption?.status === 'approved') {
            const prevEntry = parseContentUrls(revisionRequested?.contentUrls)?.[d.id]
            const prevCaption =
              (prevEntry && !('sameAsDeliverableId' in prevEntry) ? prevEntry.caption : null) ?? ''
            if (existing.caption === '' && prevCaption) {
              existing.caption = prevCaption
            }
          }
        }
        // Seed sameAsDeliverableId from the deliverable definition on first load
        if (existing.sameAsDeliverableId === null && d.sameAsDeliverableId) {
          existing.sameAsDeliverableId = d.sameAsDeliverableId
        }
        next[d.id] = existing
      }
      return next
    })
  }, [
    deliverableRevisionNotes,
    hasDeliverables,
    revisionRequested?.contentUrls,
    structuredDeliverables,
  ])

  // Clean up object URLs on unmount / file change
  useEffect(() => {
    const previewUrls = previewUrlsRef.current
    return () => {
      for (const previewUrl of previewUrls) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [])

  // ── Helpers ────────────────────────────────────────────────────────────────

  const updateDeliverableState = (id: string, updates: Partial<DeliverableUploadState>) => {
    setDeliverableStates((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? emptyDeliverableState()), ...updates },
    }))
  }

  const handleFileSelect = (deliverableId: string, file: File) => {
    const prev = deliverableStates[deliverableId]
    if (prev?.previewUrl) {
      URL.revokeObjectURL(prev.previewUrl)
      previewUrlsRef.current.delete(prev.previewUrl)
    }
    const previewUrl = URL.createObjectURL(file)
    previewUrlsRef.current.add(previewUrl)
    updateDeliverableState(deliverableId, {
      selectedFile: file,
      previewUrl,
      acceptedSuggestion: null,
    })
  }

  const getDeliverableRevision = (deliverableId: string) =>
    getDeliverableReviewNote(deliverableRevisionNotes, deliverableId)

  const getReusedVideoUrl = (deliverableId: string): string | null => {
    const dnotes = getDeliverableRevision(deliverableId)
    if (!dnotes || dnotes.video?.status !== 'approved') return null
    return getDeliverableVideoUrl(parseContentUrls(revisionRequested?.contentUrls), deliverableId)
  }

  // ── Same-as selection (local only — applied at submit time) ───────────────
  const handleSetSameAs = (deliverableId: string, sameAsId: string | null) => {
    updateDeliverableState(deliverableId, { sameAsDeliverableId: sameAsId })
  }

  // ── Save deliverables (setup flow) ─────────────────────────────────────────
  const handleSaveDeliverables = async () => {
    if (!id || !draftDeliverables.length) return
    try {
      await updateDeliverables({ campaignId: id, deliverables: draftDeliverables }).unwrap()
      toast('Deliverables saved')
      await refetch()
    } catch {
      toast.error('Could not save deliverables. Please try again.')
    }
  }

  // ── Submit all deliverables ────────────────────────────────────────────────
  const handleUploadAll = async () => {
    if (!videoReviewMilestone || !id || !structuredDeliverables) return

    const deliverablesPayload: Record<string, any> = {}
    let anyUploading = false

    for (const d of structuredDeliverables) {
      const state = deliverableStates[d.id] ?? emptyDeliverableState()
      // Reused content (local state takes precedence over deliverable definition)
      const effectiveSameAs = state.sameAsDeliverableId ?? d.sameAsDeliverableId
      if (effectiveSameAs) {
        deliverablesPayload[d.id] = { sameAsDeliverableId: effectiveSameAs }
        continue
      }

      const reusedUrl = getReusedVideoUrl(d.id)

      if (!reusedUrl && !state.acceptedSuggestion && !state.selectedFile) {
        toast.error(`Missing video for deliverable #${structuredDeliverables.indexOf(d) + 1}`, {
          description: 'Please select a video file for each deliverable.',
        })
        return
      }

      anyUploading = !reusedUrl && !state.acceptedSuggestion
    }

    // Set uploading state for all relevant deliverables
    if (anyUploading) {
      const updates: Record<string, Partial<DeliverableUploadState>> = {}
      for (const d of structuredDeliverables) {
        const state = deliverableStates[d.id] ?? emptyDeliverableState()
        const effectiveSameAs = state.sameAsDeliverableId ?? d.sameAsDeliverableId
        if (!effectiveSameAs) {
          const reused = getReusedVideoUrl(d.id)
          if (!reused && !state.acceptedSuggestion && state.selectedFile) {
            updates[d.id] = { uploading: true, uploadProgress: 0 }
          }
        }
      }
      setDeliverableStates((prev) => {
        const next = { ...prev }
        for (const [did, upd] of Object.entries(updates)) {
          next[did] = { ...(prev[did] ?? emptyDeliverableState()), ...upd }
        }
        return next
      })
    }

    try {
      for (const d of structuredDeliverables) {
        const state = deliverableStates[d.id] ?? emptyDeliverableState()
        const effectiveSameAs = state.sameAsDeliverableId ?? d.sameAsDeliverableId
        if (effectiveSameAs) continue

        const reusedUrl = getReusedVideoUrl(d.id)

        let videoUrl: string
        let videoMeta: Record<string, any> = {}

        if (reusedUrl) {
          videoUrl = reusedUrl
          const prevUrls = revisionRequested?.contentUrls as any
          videoMeta = prevUrls?.deliverables?.[d.id]?.metadata ?? {}
        } else if (state.acceptedSuggestion) {
          videoUrl = state.acceptedSuggestion.videoUrl
          videoMeta = {
            filename: state.acceptedSuggestion.filename,
            fileSize: state.acceptedSuggestion.fileSize,
            contentType: state.acceptedSuggestion.contentType,
          }
        } else {
          const uploadResult = await dispatch(
            uploadVideoForMilestone({
              campaignId: id,
              milestoneId: videoReviewMilestone.id,
              file: state.selectedFile!,
              onProgress: (progress) => updateDeliverableState(d.id, { uploadProgress: progress }),
            }),
          ).unwrap()
          updateDeliverableState(d.id, { uploadProgress: 100 })
          videoUrl = uploadResult.url
          videoMeta = {
            uploadId: uploadResult.uploadId,
            filename: state.selectedFile!.name,
            fileSize: state.selectedFile!.size,
            contentType: state.selectedFile!.type,
          }
        }

        deliverablesPayload[d.id] = {
          video: videoUrl,
          caption: state.caption.trim() || null,
          metadata: videoMeta,
        }
      }

      await submitMilestone({
        campaignId: id,
        milestoneId: videoReviewMilestone.id,
        submissionData: {
          content_description: 'Draft video for review',
          content_urls: { deliverables: deliverablesPayload },
        },
      }).unwrap()

      toast('Video Submitted', {
        description: 'Your submission has been sent for brand review',
      })

      setDeliverableStates({})
      await refetch()
    } catch (error: any) {
      toast.error('Submission Failed', {
        description: error || 'Failed to submit. Please try again.',
      })
    } finally {
      setDeliverableStates((prev) => {
        const next = { ...prev }
        for (const key of Object.keys(next)) {
          next[key] = { ...next[key], uploading: false, uploadProgress: 0 }
        }
        return next
      })
    }
  }

  const handleDelete = async () => {
    if (!activeVideoSubmission || !videoReviewMilestone || !id) return
    if (!confirm('Are you sure you want to delete this submission? This action cannot be undone.'))
      return
    setDeleting(true)
    try {
      await deleteMilestoneSubmission({
        campaignId: id,
        milestoneId: videoReviewMilestone.id,
        submissionId: activeVideoSubmission.id,
      }).unwrap()
      toast('Submission Deleted')
    } catch (error: any) {
      toast.error('Delete Failed', { description: error || 'Failed to delete submission.' })
    } finally {
      setDeleting(false)
    }
  }

  const handleDownloadVideo = async (videoUrl: string, filename?: string) => {
    const fileName =
      filename || inferFileNameFromUrl(videoUrl, `${campaign?.brand.name}-video-review.mp4`)
    try {
      setDownloadingVideo(true)
      await downloadRemoteFile({ url: videoUrl, fileName })
      toast('Video Downloaded')
    } catch (error: any) {
      toast.error('Download Failed', {
        description: error?.message || 'Could not download video.',
      })
    } finally {
      setDownloadingVideo(false)
    }
  }

  const platformLabel = Array.isArray(campaign?.platform)
    ? campaign.platform.join(', ')
    : campaign?.platform

  // ── Loading / error states ─────────────────────────────────────────────────

  if (detailLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#C0FD72]" />
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold mb-4 text-white">Campaign Not Found</h2>
        <p className="mb-4 text-gray-300">The campaign you're looking for doesn't exist.</p>
        <Link to="/" className="text-blue-500 hover:underline">
          Return to Dashboard
        </Link>
      </div>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <Link
        to={`/collaborations/${campaign.id}`}
        className="text-gray-400 hover:text-white inline-flex items-center gap-2 text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Collaboration Details
      </Link>

      {/* ── No deliverables: setup section ── */}
      {!hasDeliverables && !activeVideoSubmission && (
        <div className="rounded-2xl border border-white/10 bg-white/4 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <ListVideo className="h-5 w-5 text-[#C0FD72] mt-0.5 shrink-0" />
            <div>
              <p className="text-white font-semibold text-sm">Set up your deliverables</p>
              <p className="text-gray-400 text-xs mt-0.5">
                Define what content you'll be submitting. You can do this yourself — no need to
                wait for your manager.
              </p>
            </div>
          </div>
          <DeliverableBuilder value={draftDeliverables} onChange={setDraftDeliverables} />
          <Button
            variant="lime"
            onClick={handleSaveDeliverables}
            disabled={isSavingDeliverables || !draftDeliverables.length}
            className="w-full rounded-xl"
          >
            {isSavingDeliverables ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              'Save Deliverables & Continue'
            )}
          </Button>
        </div>
      )}

      {/* ── Revision feedback banner ── */}
      {isRevisionRequested && hasDeliverables && (
        <div className="bg-[#DA9B8E] rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full border-2 border-black/20 flex items-center justify-center">
              <ClipboardPen className="h-5 w-5 text-black/70" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-black text-sm mb-1.5">
                Brand Feedback — Changes Requested
              </p>
              {deliverableRevisionNotes ? (
                <div className="space-y-1 text-sm text-black/80">
                  {structuredDeliverables!.map((d, i) => {
                    const dnotes = deliverableRevisionNotes[d.id]
                    if (!dnotes) return null
                    return (
                      <div key={d.id}>
                        <span className="font-semibold text-black">
                          #{i + 1} {d.platform} {d.format}:{' '}
                        </span>
                        {dnotes.video?.status === 'revision_requested' && (
                          <span>Video — {dnotes.video.comment || 'Changes requested'}. </span>
                        )}
                        {dnotes.caption?.status === 'revision_requested' && (
                          <span>Caption — {dnotes.caption.comment || 'Changes requested'}.</span>
                        )}
                        {dnotes.video?.status === 'approved' &&
                          dnotes.caption?.status === 'approved' && (
                            <span className="text-green-800">All approved ✓</span>
                          )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-black/80">Please review and resubmit your video.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Status banner ── */}
      {activeVideoSubmission && (
        <div
          className={`rounded-2xl px-5 py-3 flex items-center justify-between ${
            isVideoApproved ? 'bg-green-500/15 border border-green-500/25' : 'bg-[#F4DB66]'
          }`}
        >
          <span
            className={`text-sm font-medium ${isVideoApproved ? 'text-green-300' : 'text-black'}`}
          >
            {isVideoApproved
              ? '✅ Video approved! Your video has been accepted by the brand.'
              : 'Your video has been submitted and is awaiting brand review.'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className={`rounded-full h-8 w-8 ${isVideoApproved ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/10 hover:bg-black/20 text-black'}`}
          >
            <Bell className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* ── Submitted state: review panel ── */}
      {activeVideoSubmission && (
        <div className="space-y-4">
          <div className="bg-white/6 rounded-2xl border border-white/10 p-5 space-y-4">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-1">
                Submitted
              </p>
              <p className="text-white text-sm">
                {new Date(activeVideoSubmission.submittedAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </p>
            </div>

            {/* Per-deliverable submitted content */}
            {(() => {
              const urls = activeVideoSubmission.contentUrls as any
              const deliverables = urls?.deliverables as Record<string, any> | undefined
              if (!deliverables || !structuredDeliverables) return null
              return structuredDeliverables.map((d, i) => {
                const entry = deliverables[d.id]
                if (!entry) return null
                if (entry.sameAsDeliverableId) {
                  return (
                    <div key={d.id} className="pt-2 border-t border-white/5">
                      <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-1">
                        #{i + 1} {d.platform} {d.format}
                      </p>
                      <p className="text-gray-400 text-xs">
                        Same content as #
                        {structuredDeliverables.findIndex(
                          (x) => x.id === entry.sameAsDeliverableId,
                        ) + 1}
                      </p>
                    </div>
                  )
                }
                return (
                  <div key={d.id} className="pt-2 border-t border-white/5 space-y-2">
                    <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold">
                      #{i + 1} {d.platform} {d.format}
                    </p>
                    {entry.video && (
                      <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-white/10">
                        <video
                          controls
                          className="absolute inset-0 w-full h-full object-contain"
                          src={entry.video}
                        />
                      </div>
                    )}
                    {entry.caption && (
                      <p className="text-white text-sm whitespace-pre-wrap">{entry.caption}</p>
                    )}
                    {entry.video && (
                      <Button
                        type="button"
                        onClick={() =>
                          void handleDownloadVideo(entry.video, entry.metadata?.filename)
                        }
                        disabled={downloadingVideo}
                        variant="outline"
                        size="sm"
                        className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white rounded-xl"
                      >
                        {downloadingVideo ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                        Download
                      </Button>
                    )}
                  </div>
                )
              })
            })()}
          </div>

          {!isVideoApproved ? (
            <div className="bg-yellow-500/8 border border-yellow-500/20 rounded-2xl p-4">
              <p className="text-yellow-300 text-xs leading-relaxed">
                <strong className="block mb-0.5">What happens next?</strong>
                The brand will review your video and either approve it or request changes.
              </p>
            </div>
          ) : (
            <div className="bg-green-500/8 border border-green-500/20 rounded-2xl p-4">
              <p className="text-green-300 text-xs leading-relaxed">
                <strong className="block mb-0.5">✅ Approved!</strong>
                Your video has been accepted. Proceed to the next milestone.
              </p>
            </div>
          )}

          <div className="bg-white/6 rounded-2xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Review Timeline</h3>
            <SubmissionTimeline
              submissionId={activeVideoSubmission.id}
              milestoneId={videoReviewMilestone!.id}
              currentUserRole="athlete"
            />
          </div>

          {!isVideoApproved && (
            <Button
              onClick={handleDelete}
              disabled={deleting}
              variant="outline"
              className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-xl"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" /> Delete Submission
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* ── Upload state: per-deliverable cards ── */}
      {!activeVideoSubmission && hasDeliverables && conceptSubmission && (
        <div className="space-y-4">
          {/* Brand identity strip */}
          <div className="flex items-center gap-3 px-1">
            <div className="h-9 w-9 rounded-full overflow-hidden bg-white/10 border border-white/15 shrink-0">
              <img
                src={campaign.brand.logo}
                alt={campaign.brand.name}
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <p className="text-white font-semibold leading-tight">{campaign.brand.name}</p>
              {platformLabel && <p className="text-gray-400 text-xs">{platformLabel}</p>}
            </div>
          </div>

          {isConceptPendingReview && !isConceptApproved && (
            <div className="bg-yellow-500/8 border border-yellow-500/20 rounded-2xl p-4">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                <p className="text-yellow-200/70 text-xs leading-relaxed">
                  Your concept is under review. You can upload your video now, but approval may
                  depend on concept sign-off.
                </p>
              </div>
            </div>
          )}

          {/* Per-deliverable cards */}
          {structuredDeliverables!.map((d, index) => {
            const state = deliverableStates[d.id] ?? emptyDeliverableState()
            const effectiveSameAs = state.sameAsDeliverableId ?? d.sameAsDeliverableId
            const dnotes = getDeliverableRevision(d.id)
            const reusedUrl = getReusedVideoUrl(d.id)
            const videoNeedsChange = !dnotes || dnotes.video?.status !== 'approved'
            const captionNeedsChange = !dnotes || dnotes.caption?.status !== 'approved'
            const otherDeliverables = structuredDeliverables!.filter((_, i) => i !== index)

            const sameAsSelect = otherDeliverables.length > 0 && (
              <Select
                value={effectiveSameAs ?? '__none__'}
                onValueChange={(val) => handleSetSameAs(d.id, val === '__none__' ? null : val)}
              >
                <SelectTrigger
                  className={`h-7 w-auto text-xs gap-1.5 pr-2 [&>svg]:h-3 [&>svg]:w-3 ${
                    effectiveSameAs
                      ? 'border-[#C0FD72]/20 bg-[#C0FD72]/10 text-[#C0FD72]'
                      : 'border-white/10 bg-white/5 text-gray-400'
                  }`}
                >
                  <SelectValue placeholder="Unique content" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unique content</SelectItem>
                  {otherDeliverables.map((other) => {
                    const otherNumber = structuredDeliverables!.indexOf(other) + 1
                    return (
                      <SelectItem key={other.id} value={other.id}>
                        Same as #{otherNumber} — {other.platform} {other.format}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            )

            // sameAs deliverable — no upload needed
            if (effectiveSameAs) {
              const sourceIndex = structuredDeliverables!.findIndex(
                (x) => x.id === effectiveSameAs,
              )
              return (
                <div
                  key={d.id}
                  className="rounded-2xl border border-[#C0FD72]/20 bg-[#C0FD72]/5 p-4"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        #{index + 1} {d.platform} {d.format}
                      </span>
                      <span className="text-xs text-[#C0FD72]/80 bg-[#C0FD72]/10 px-2 py-0.5 rounded-full">
                        Same content as #{sourceIndex + 1}
                      </span>
                    </div>
                    {sameAsSelect}
                  </div>
                  <p className="text-gray-400 text-xs">
                    This deliverable reuses the video from deliverable #{sourceIndex + 1}. No
                    separate upload needed.
                  </p>
                </div>
              )
            }

            const deliverableSuggestion = getSuggestionForDeliverable(d.id)

            return (
              <div
                key={d.id}
                className="rounded-2xl border border-white/10 bg-white/3 p-5 space-y-4"
              >
                {/* Header */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      #{index + 1} {d.platform} {d.format}
                    </span>
                    {dnotes?.video?.status === 'approved' && (
                      <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
                        Video approved
                      </span>
                    )}
                  </div>
                  {sameAsSelect}
                </div>

                {/* Per-deliverable manager suggestion banner */}
                {deliverableSuggestion &&
                  !state.acceptedSuggestion &&
                  !state.selectedFile &&
                  !reusedUrl &&
                  (deliverableSuggestion.sameAsDeliverableId ? (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                          <Video className="w-3 h-3 text-blue-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-semibold">
                            Manager suggests reusing another deliverable's video
                          </p>
                          <p className="text-blue-200/60 text-xs truncate mt-0.5">
                            {(() => {
                              const sourceIndex = structuredDeliverables!.findIndex(
                                (x) => x.id === deliverableSuggestion.sameAsDeliverableId,
                              )
                              return sourceIndex >= 0
                                ? `Same as deliverable #${sourceIndex + 1}`
                                : 'Same as another deliverable'
                            })()}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() =>
                            handleSetSameAs(d.id, deliverableSuggestion.sameAsDeliverableId!)
                          }
                          variant="lime"
                          className="shrink-0 text-xs h-7 px-3"
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                          <Video className="w-3 h-3 text-blue-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-semibold">
                            Manager uploaded a video
                          </p>
                          <p className="text-blue-200/60 text-xs truncate mt-0.5">
                            {deliverableSuggestion.filename}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => setShowSuggestionSheet(d.id)}
                          variant="lime"
                          className="shrink-0 text-xs h-7 px-3"
                        >
                          Review
                        </Button>
                      </div>
                    </div>
                  ))}

                {/* Revision feedback for this deliverable */}
                {isRevisionRequested && dnotes && (
                  <div className="text-xs text-black/80 bg-[#DA9B8E]/30 rounded-xl p-3 space-y-1">
                    {dnotes.video?.status === 'revision_requested' && (
                      <p>
                        <span className="font-semibold">Video: </span>
                        {dnotes.video.comment || 'Changes requested'}
                      </p>
                    )}
                    {dnotes.caption?.status === 'revision_requested' && (
                      <p>
                        <span className="font-semibold">Caption: </span>
                        {dnotes.caption.comment || 'Changes requested'}
                      </p>
                    )}
                  </div>
                )}

                {/* Video area */}
                {!videoNeedsChange && reusedUrl ? (
                  /* Video was approved — show it locked */
                  <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-green-500/30">
                    <video
                      controls
                      className="absolute inset-0 w-full h-full object-contain"
                      src={reusedUrl}
                    />
                    <div className="absolute top-3 left-3">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-500/80 backdrop-blur-xs text-white">
                        <CheckCircle className="h-3 w-3" /> Video Approved
                      </div>
                    </div>
                  </div>
                ) : state.previewUrl ? (
                  /* File selected preview */
                  <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-white/10">
                    <video
                      controls
                      className="absolute inset-0 w-full h-full object-contain"
                      src={state.previewUrl}
                    />
                    <label
                      htmlFor={`video-upload-${d.id}`}
                      className="absolute bottom-3 right-3 cursor-pointer text-xs bg-black/70 text-[#C0FD72] hover:text-[#b0ef62] px-3 py-1.5 rounded-full backdrop-blur-xs border border-[#C0FD72]/20 transition-colors"
                    >
                      <Upload className="h-3 w-3 inline mr-1" />
                      Change
                    </label>
                  </div>
                ) : isConceptApproved ? (
                  /* Upload dropzone */
                  <div className="aspect-video">
                    <label
                      htmlFor={`video-upload-${d.id}`}
                      className="flex flex-col items-center justify-center w-full h-full rounded-xl border-2 border-dashed border-white/20 hover:border-[#C0FD72]/40 bg-white/3 hover:bg-[#C0FD72]/3 transition-all cursor-pointer group"
                    >
                      <Upload className="w-6 h-6 text-gray-400 group-hover:text-[#C0FD72] transition-colors mb-2" />
                      <p className="text-white font-semibold text-sm">Drop your video here</p>
                      <p className="text-gray-500 text-xs mt-1">MP4 or MOV · tap to browse</p>
                    </label>
                  </div>
                ) : (
                  /* Concept not approved yet */
                  <div className="aspect-video bg-white/3 rounded-xl border border-white/10 flex items-center justify-center">
                    <div className="text-center px-6">
                      <Bell className="h-8 w-8 text-yellow-500/60 mx-auto mb-2" />
                      <p className="text-white font-semibold text-sm">
                        Waiting on Concept Approval
                      </p>
                    </div>
                  </div>
                )}

                <input
                  type="file"
                  accept="video/*"
                  id={`video-upload-${d.id}`}
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleFileSelect(d.id, e.target.files[0])
                  }}
                />

                {/* File name strip */}
                {(state.selectedFile || state.acceptedSuggestion) && (
                  <div className="flex items-center gap-2 px-1">
                    <Video className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="text-xs text-gray-300 truncate">
                      {state.acceptedSuggestion?.filename ?? state.selectedFile?.name}
                    </span>
                  </div>
                )}

                {/* Caption */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-white uppercase text-[10px] font-bold tracking-widest">
                      Post Caption & Notes
                      {isRevisionRequested && !captionNeedsChange ? ' — Approved' : ' (optional)'}
                    </label>
                    {captionNeedsChange && (
                      <button
                        type="button"
                        onClick={() => setShowCaptionChat(d.id)}
                        className="flex items-center gap-1 text-[10px] text-[#C0FD72] hover:text-[#b0ef62] transition-colors"
                      >
                        <Sparkles className="w-3 h-3" /> AI assist
                      </button>
                    )}
                  </div>
                  {!captionNeedsChange ? (
                    <div className="relative">
                      <div className="min-h-[72px] bg-green-500/10 border border-green-500/25 rounded-xl p-3 text-sm text-white/80 whitespace-pre-wrap">
                        {state.caption}
                      </div>
                      <span className="absolute top-2 right-2 text-[10px] text-green-400 font-medium flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> Approved
                      </span>
                    </div>
                  ) : (
                    <Textarea
                      value={state.caption}
                      onChange={(e) => updateDeliverableState(d.id, { caption: e.target.value })}
                      placeholder="Write your post caption here for brand review"
                      className="min-h-[80px] bg-white/5 border-white/15 text-white placeholder:text-gray-500 resize-none rounded-xl text-sm"
                    />
                  )}
                </div>

                {/* Upload progress */}
                {state.uploading && state.uploadProgress > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Uploading…</span>
                      <span>{state.uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5">
                      <div
                        className="bg-[#C0FD72] h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${state.uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )
          })}

          {/* Global submit button */}
          <Button
            onClick={handleUploadAll}
            disabled={
              isSubmitting ||
              Object.values(deliverableStates).some((s) => s.uploading) ||
              !isConceptApproved
            }
            variant="lime"
            className="w-full rounded-xl"
            size="lg"
          >
            {isSubmitting || Object.values(deliverableStates).some((s) => s.uploading) ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
              </>
            ) : isRevisionRequested ? (
              'Submit Revision'
            ) : (
              'Submit Video for Review'
            )}
          </Button>
        </div>
      )}

      {/* ── No concept yet ── */}
      {!activeVideoSubmission && !conceptSubmission && hasDeliverables && (
        <div className="aspect-video bg-white/3 rounded-2xl border border-white/10 flex items-center justify-center">
          <div className="text-center px-6">
            <AlertCircle className="h-10 w-10 text-yellow-500/60 mx-auto mb-3" />
            <p className="text-white font-semibold text-sm">Creative Concept Required</p>
            <p className="text-gray-400 text-xs mt-1 mb-4">
              Submit your creative concept before uploading video
            </p>
            <Link to={`/collaborations/${campaign.id}/concept`}>
              <Button size="sm" variant="lime">
                Submit Creative Concept
              </Button>
            </Link>
          </div>
        </div>
      )}

      {showSuggestionSheet &&
        (() => {
          const raw = getSuggestionForDeliverable(showSuggestionSheet)
          // "Same as" suggestions have no video of their own to preview/accept —
          // only real uploads open the review sheet.
          if (!raw || raw.sameAsDeliverableId || !raw.videoUrl) return null
          const suggestion = raw as VideoSuggestion
          return (
            <VideoSuggestionSheet
              suggestion={suggestion}
              onAccept={(caption) => {
                updateDeliverableState(showSuggestionSheet, {
                  acceptedSuggestion: suggestion,
                  caption: caption?.trim() || '',
                  selectedFile: null,
                })
                setShowSuggestionSheet(null)
              }}
              onClose={() => setShowSuggestionSheet(null)}
            />
          )
        })()}

      {showCaptionChat && id && campaign && (
        <AiCaptionAssistantChat
          campaignId={id}
          brandName={campaign.brand.name}
          platform={platformLabel || 'social media'}
          onApplyCaption={(value) => {
            if (showCaptionChat) {
              updateDeliverableState(showCaptionChat, { caption: value })
            }
            setShowCaptionChat(null)
          }}
          onClose={() => setShowCaptionChat(null)}
        />
      )}
    </div>
  )
}

export default UploadVideoReview
